<template>
  <a
    :href="formatLink(estate.permalink)"
    data-component="card"
    class="relative block h-full p-5 transition-all duration-300 hover:bg-brand-500 hover:text-white group hover:shadow-2xl"
  >
    <div
      class="absolute top-0 left-0 z-10 m-8 text-sm tracking-wider text-white uppercase lg:m-10"
    >
      <ul class="flex flex-wrap -m-2">
        <li
          v-if="
            estate.meta.estate.status === 'sold' ||
              estate.meta.estate.status === 'rented' ||
              estate.meta.estate.status === 'option' ||
              estate.meta.estate.status === 'under_contract'
          "
          class="p-2"
        >
          <div class="p-2 rounded-md bg-danger-500">
            <template v-if="estate.meta.estate.status === 'sold'">{{
              locales[estateData.lang].sold
            }}</template>
            <template v-if="estate.meta.estate.status === 'rented'">{{
              locales[estateData.lang].rented
            }}</template>
            <template v-if="estate.meta.estate.status === 'option'">{{
              locales[estateData.lang].option
            }}</template>
            <template v-if="estate.meta.estate.status === 'under_contract'">{{
              locales[estateData.lang].under_contract
            }}</template>
          </div>
        </li>
        <li
          v-if="
            estate.meta.open_homes.hasOpenHome &&
              estate.meta.estate.status === 'available'
          "
          class="p-2"
        >
          <div class="p-2 rounded-md bg-danger-500">
            {{ locales[estateData.lang].open_home }}
          </div>
        </li>
      </ul>
    </div>

    <vue-glide
      v-if="estate.meta.features.images.length > 0"
      :options="glideOptions"
    >
      <template v-if="estate.meta.estate.status === 'available'">
        <vue-glide-slide
          v-for="image in estate.meta.features.images.slice(0, 5)"
          :key="image.ID"
        >
          <div class="aspect-ratio-4/3" />
          <div
            class="absolute top-0 left-0 z-10 w-full h-full transition duration-200 bg-black bg-opacity-50 opacity-0 lg:group-hover:opacity-100"
          />
          <img
            class="absolute top-0 left-0 z-0 object-cover object-center w-full h-full transition-all duration-200 transform group-hover:scale-105 lazy"
            :data-src="image.sizes.thumbnail"
            :alt="`Condrogest - ${estate.meta.estate.title[estateData.lang]}`"
          >
        </vue-glide-slide>
      </template>

      <template v-else>
        <vue-glide-slide>
          <div class="aspect-ratio-4/3" />
          <div
            class="absolute top-0 left-0 z-10 w-full h-full bg-brand-500 opacity-40"
          />
          <div class="absolute top-0 left-0 w-full h-full opacity-50">
            <img
              class="absolute top-0 left-0 z-0 object-cover object-center w-full h-full lazy"
              :data-src="estate.meta.features.images[0].sizes.thumbnail"
              :alt="`Condrogest - ${estate.meta.estate.title[estateData.lang]}`"
            >
          </div>
        </vue-glide-slide>
      </template>

      <template
        v-if="
          estate.meta.features.images.length > 1 &&
            estate.meta.estate.status === 'available'
        "
        slot="control"
      >
        <button
          class="px-5 py-16 transition duration-150 pointer-events-auto focus:outline-none glide__arrow glide__arrow--left active:opacity-50"
          data-glide-dir="<"
          @click.stop="prevent"
        >
          <IconLeft />
        </button>
        <button
          class="px-5 py-16 transition duration-150 pointer-events-auto focus:outline-none glide__arrow glide__arrow--right active:opacity-50"
          data-glide-dir=">"
          @click.stop="prevent"
        >
          <IconRight />
        </button>
      </template>
    </vue-glide>

    <div class="flex justify-between mt-4 mb-3">
      <p class="mt-1 text-xs tracking-wider uppercase group-hover:opacity-50">
        {{ formatType(estate.meta.features.type) }}
        {{ formatNegotiation(estate.meta.features.negotiation) }}
      </p>
      <div
        v-if="!estate.meta.location.hidden"
        class="text-base"
      >
        <i class="fas fa-map-marker-alt text-danger-500" />
        {{ estate.meta.location.city }}
      </div>
    </div>

    <h3 class="mb-3 lg:text-xl">
      {{ estate.meta.estate.title[estateData.lang] }}
    </h3>

    <div
      v-if="!estate.meta.price.hidden"
      class="mb-3"
    >
      <p v-if="!estate.meta.estate.is_project">
        &euro;
        {{ $formatNumber(estate.meta.price.amount, "DOT") }}
      </p>
      <p v-else>
        <span
          v-if="estate.meta.price.min_max.min && estate.meta.price.min_max.max"
        >
          &euro; {{ $formatNumber(estate.meta.price.min_max.min, "DOT") }} -
          &euro; {{ $formatNumber(estate.meta.price.min_max.max, "DOT") }}
        </span>
        <span
          v-if="estate.meta.price.min_max.min && !estate.meta.price.min_max.max"
        >
          &euro; {{ $formatNumber(estate.meta.price.min_max.min, "DOT") }}
        </span>
      </p>
    </div>

    <div class="flex items-center justify-between space-x-5">
      <div>
        <img
          v-if="estate.meta.energy_details.epc_category"
          class="h-5 lazy"
          :data-src="`/wp-content/themes/condrogest/assets/img/energy/${
            estateData.lang
          }/${formatEPC(estate.meta.energy_details.epc_category)}.png`"
        >
      </div>
      <ul class="flex space-x-6">
        <li
          v-if="parseInt(estate.meta.facilities.bedrooms) >= 1"
          class="text-base"
        >
          <i class="mr-1 text-danger-500 fal fa-bed" />
          <span>{{ estate.meta.facilities.bedrooms }}</span>
        </li>
        <li
          v-if="parseInt(estate.meta.sizes.liveable_area.size) >= 1"
          class="text-base"
        >
          <i class="mr-1 text-danger-500 fal fa-home-alt" />
          <span>{{
            $formatNumber(estate.meta.sizes.liveable_area.size, "DOT")
          }}㎡</span>
        </li>
        <li
          v-if="parseInt(estate.meta.sizes.plot_area.size) >= 1"
          class="text-base"
        >
          <i class="mr-1 text-danger-500 fal fa-expand-arrows" />
          <span>{{
            $formatNumber(estate.meta.sizes.plot_area.size, "DOT")
          }}㎡</span>
        </li>
      </ul>
    </div>
  </a>
</template>

<script>
import LazyLoad from "vanilla-lazyload";
import { Glide, GlideSlide } from "vue-glide-js";

import IconLeft from "../svg/IconLeft";
import IconRight from "../svg/IconRight";

const slugify = require("slugify");

export default {
  components: {
    IconLeft,
    IconRight,
    [Glide.name]: Glide,
    [GlideSlide.name]: GlideSlide,
  },
  props: ["loading", "estate", "estateData", "locales"],
  data() {
    return {
      glideOptions: {
        type: "carousel",
        perView: 1,
        gap: 0,
        keyboard: false,
      },
    };
  },
  mounted() {
    new LazyLoad({
      elements_selector: ".lazy",
      unobserve_entered: true,
    });
  },
  methods: {
    prevent() {
      return false;
    },
    formatLink(link) {
      const title = slugify(
        this.estate.meta.estate.title[this.estateData.lang],
        {
          lower: true,
          strict: true,
        }
      );
      const url = link.split("/");
      let prefix = "";
      if (this.estateData.lang === "nl") {
        prefix = "te-huur";
        if (this.estate.meta.features.negotiation === "sale") {
          prefix = "te-koop";
        }
      } else if (this.estateData.lang === "fr") {
        prefix = "a-louer";
        if (this.estate.meta.features.negotiation === "sale") {
          prefix = "a-vendre";
        }
      }
      const str = `/${this.estateData.lang}/${prefix}/${url[5]}/${title}`;
      return str;
    },
    formatType(type) {
      let output = "";
      switch (type) {
        case "apartment":
          if (this.estateData.lang === "nl") {
            output = "Appartement";
          } else if (this.estateData.lang === "fr") {
            output = "Appartement";
          }
          break;
        case "commercial":
          if (this.estateData.lang === "nl") {
            output = "Winkelpand";
          } else if (this.estateData.lang === "fr") {
            output = "Magasin";
          }
          break;
        case "house":
          if (this.estateData.lang === "nl") {
            output = "Huis";
          } else if (this.estateData.lang === "fr") {
            output = "Maison";
          }
          break;
        case "land":
          if (this.estateData.lang === "nl") {
            output = "Grond";
          } else if (this.estateData.lang === "fr") {
            output = "Terrain";
          }
          break;
        case "office":
          if (this.estateData.lang === "nl") {
            output = "Kantoor";
          } else if (this.estateData.lang === "fr") {
            output = "Office";
          }
          break;
        case "parking":
          if (this.estateData.lang === "nl") {
            output = "Parking";
          } else if (this.estateData.lang === "fr") {
            output = "Parking";
          }
          break;
        default:
          break;
      }
      return output;
    },
    formatNegotiation(negotiation) {
      let output = "";
      switch (negotiation) {
        case "sale":
          if (this.estateData.lang === "nl") {
            output = "te koop";
          } else if (this.estateData.lang === "fr") {
            output = "à vendre";
          }
          break;
        case "let":
          if (this.estateData.lang === "nl") {
            output = "te huur";
          } else if (this.estateData.lang === "fr") {
            output = "à louer";
          }
          break;
        default:
          break;
      }
      return output;
    },
    formatEPC(label) {
      if (label === "A++" && this.estateData.lang === "nl") {
        label = "a+";
      }
      if (label === "G" && this.estateData.lang === "nl") {
        label = "f";
      }
      return label.toLowerCase();
    },
  },
};
</script>

<style>
[data-component="card"] .glide {
  @apply relative mb-4;
}
[data-component="card"] .glide__track {
  @apply relative z-0;
}
[data-component="card"] .glide__slide {
  @apply relative overflow-hidden;
}
[data-component="card"] [data-glide-el="controls"] {
  @apply absolute top-0 left-0 z-10 flex items-center justify-between w-full h-full px-5 transition duration-500 opacity-0 pointer-events-none pr-0 pl-0;
}
[data-component="card"]:hover [data-glide-el="controls"] {
  @apply opacity-100;
}
[data-component="card"] img.lazy:not(.nofade):not(.initial) {
  transition-property: all;
}
</style>
